import { ChangeDetectionStrategy, Component, computed, OnDestroy, OnInit, signal } from '@angular/core';
import { EventCalendar, generateGoogleCalendarLink, generateICS } from 'app/utils/generate-calendar-event';
import { Loggable } from 'app/utils/logging/loggable';
import SocialEvent from 'entity/SocialEvent';
import { firstValueFrom, ReplaySubject, takeUntil } from 'rxjs';
import { EventsService } from '../../services/events.service';
import { ActivatedRoute } from '@angular/router';
import { BadgingService } from 'app/modules/social/services/badging/badging.service';
import BadgeData from 'entity/BadgeData';
import { VideoChatService } from 'app/modules/social/services/videochat.service';
import { SocialPageService } from 'app/modules/social/social-page/services/social-page.service';
import BadgeQuestion from 'entity/BadgeQuestion';
import { DevicePermissionService } from 'app/modules/social/services/device-permission/device-permission.service';

@Component({
  selector: 'app-event-countdown-page',
  templateUrl: './event-countdown-page.component.html',
  styleUrl: './event-countdown-page.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventCountdownPageComponent extends Loggable implements OnDestroy, OnInit {
  private destroyed$ = new ReplaySubject<boolean>(1);
  protected currentYear?: number;
  protected loading = signal<boolean>(false);
  public event = signal<SocialEvent | null>(null);
  private userRegistrationId?: string;
  private eventId?: string | null;
  public showJoinButton = signal<boolean>(false);
  private intervalId!: number;
  public hasCompletedDemo = signal<boolean>(false);
  public badgeInfo: BadgeData[] = [];
  public hasEmptyBadgeInfo = signal<boolean>(false);
  public hasBadgeQuestions = signal<boolean>(false);
  public reactiveClass = computed(() => ({
    'primary-btn': !this.hasCompletedDemo(),
    'secondary-btn': this.hasCompletedDemo(),
  }));
  public eventBackgroundColor = signal<string>('#091d50');

  // Countdown signals
  public daysLeft = signal<number>(0);
  public hoursLeft = signal<number>(0);
  public minutesLeft = signal<number>(0);

  public constructor(
    private eventsService: EventsService,
    private route: ActivatedRoute,
    private badgingService: BadgingService,
    private videoChatService: VideoChatService,
    private socialPageService: SocialPageService,
    private devicePermissionService: DevicePermissionService,
  ) {
    super();
    this.setDisplayName('EventCountdownPage');

    const currentDate = new Date();
    this.currentYear = currentDate.getFullYear();
  }

  public ngOnInit(): void {
    this.eventId = this.route.snapshot.paramMap.get('id');

    if (!this.eventId) {
      this.error('Error while getting the event id');
      return;
    }

    this.getSelectedEvent();
  }

  public ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private getSelectedEvent(): void {
    this.loading.set(true);

    if (!this.eventId) {
      this.error('Error while getting the event id');
      return;
    }

    this.eventsService
      .getEvent(this.eventId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (event: SocialEvent) => {
          if (event) {
            this.event.set(event);
            this.startCountdown();

            if (event.eventColor) {
              this.eventBackgroundColor.set(event.eventColor);
            }

            Promise.all([this.getBadgingData(), this.checkHasCompletedDemo(), this.getBadgeQuestions()])
              .then(() => this.loading.set(false))
              .catch((error) => {
                this.error('Error loading additional event data', error);
                this.loading.set(false);
              });
          }
          this.loading.set(false);
        },
        error: (error: unknown) => {
          this.error('Error loading event, ', error);
          this.loading.set(false);
        },
      });
  }

  public saveCalendar(type: 'google' | 'outlook') {
    const event = this.event();
    if (!event) {
      return;
    }

    const eventData: EventCalendar = {
      uid: event.socialEventId ?? '',
      summary: event.name ?? '',
      description: `${event.description ?? ''}, ${event.eventLink ?? ''}?registrationId=${this.userRegistrationId ?? ''}`,
      startDate: new Date(event.startDateTime ?? ''),
      endDate: new Date(event.endDateTime ?? ''),
      location: event.eventLink ?? '',
    };

    const content = type === 'google' ? generateGoogleCalendarLink(eventData) : generateICS(eventData);

    if (type === 'outlook') {
      const blob = new Blob([content], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      return;
    }

    window.open(content, '_blank');
  }

  public get eventName(): string {
    return this.event()?.name ?? '';
  }

  public get eventLogoImage(): string {
    return this.event()?.logoImageURI ?? 'assets/images/logos/scoot_logo_primary.png';
  }

  private startCountdown(): void {
    this.updateCountdown();
    // interval to update countdown at minute base
    this.intervalId = window.setInterval(() => {
      this.updateCountdown();
    }, 60000);
  }

  private updateCountdown() {
    const event = this.event();
    if (!event || !event.startDateTime) {
      return;
    }

    const now = new Date();
    const eventDate = new Date(event.startDateTime);

    // Normalize both dates to ignore seconds and milliseconds
    now.setSeconds(0, 0);
    eventDate.setSeconds(0, 0);

    // Check if the event has already started
    if (now >= eventDate) {
      this.daysLeft.set(0);
      this.hoursLeft.set(0);
      this.minutesLeft.set(0);
      this.showJoinButton.set(true);
      return;
    }

    // Prevent negative value
    const timeLeft = Math.abs(eventDate.getTime() - now.getTime());

    this.daysLeft.set(Math.floor(timeLeft / (1000 * 60 * 60 * 24)));
    this.hoursLeft.set(Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
    this.minutesLeft.set(Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60)));

    if (this.daysLeft() === 0 && this.hoursLeft() === 0 && this.minutesLeft() <= 5) {
      this.showJoinButton.set(true);
    }
  }

  private getBadgeQuestions(): void {
    const event = this.event();

    if (event?.socialRoomSetId) {
      this.badgingService
        .getBadgeQuestions(event.socialRoomSetId)
        .then((badgeQuestions: BadgeQuestion[]) => {
          if (badgeQuestions.length === 0) {
            this.hasBadgeQuestions.set(false);
          } else {
            this.hasBadgeQuestions.set(true);
          }
        })
        .catch((error) => {
          this.hasBadgeQuestions.set(false);
          this.error('Error while getting scootaverse badge questions, ', error);
        });
    }
  }

  private getBadgingData(): void {
    const event = this.event();

    if (event?.socialRoomSetId) {
      this.badgingService
        .getMyBadgeData(event.socialRoomSetId)
        .then((badgeData: BadgeData[]) => {
          if (badgeData) {
            const emptyBadgeData = badgeData.every(
              (badge) => !badge.value && (!badge.multiValues || badge.multiValues.length === 0),
            );
            this.hasEmptyBadgeInfo.set(emptyBadgeData);
          }
        })
        .catch((error) => {
          this.hasEmptyBadgeInfo.set(false);
          this.error('Error while getting user badging data, ', error);
        });
    }
  }

  private checkHasCompletedDemo(): void {
    this.videoChatService
      .hasCompletedDemo()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((hasCompletedDemo) => {
        this.hasCompletedDemo.set(hasCompletedDemo);
      });
  }

  public async redirectToButtonAction(step?: string): Promise<void> {
    this.loading.set(true);
    const event = this.event();
    const registrationId = this.eventsService.registrationId;

    if (event && event.socialRoomSetId && registrationId) {
      // If the user has finished the requirements to join a room, make it enter directly
      if (
        !step &&
        this.audioPermissionGranted &&
        this.videoPermissionGranted &&
        this.hasCompletedDemo() &&
        !this.hasEmptyBadgeInfo() &&
        this.hasBadgeQuestions()
      ) {
        this.eventsService.eventFlowStep = 'joinRoom';
      } else {
        this.eventsService.eventFlowStep = step;
      }
      this.eventsService.eventId = event.socialEventId;

      const socialRoomSet = await firstValueFrom(this.videoChatService.getSocialRoomSet(event.socialRoomSetId));

      if (!socialRoomSet || !socialRoomSet.socials || socialRoomSet.socials.length === 0) {
        this.error('No SocialRoomSet was found');
        this.loading.set(false);
        return;
      }

      this.socialPageService
        .onEnterRoom(socialRoomSet.socials[0], registrationId, event.name, event.endDateTime)
        .catch((e) => this.error(e));
    }
  }

  private get audioPermissionGranted(): boolean {
    return this.devicePermissionService.permission?.audioGranted;
  }

  private get videoPermissionGranted(): boolean {
    return this.devicePermissionService.permission?.videoGranted;
  }
}
